import { Badge } from '@ordo/ui/badge'
import { Button } from '@ordo/ui/button'
import { Input } from '@ordo/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ordo/ui/select'
import { Separator } from '@ordo/ui/separator'
import { useApiEndpoints, usePreviewDynamicList } from 'hooks/api'
import { Plus, Trash2 } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import type {
  ApiListField,
  AssociationField,
  DynamicListRule,
  EventField,
  ExtraDataField,
  RuleMatch,
  Ruleset,
} from 'types/communications'
import type { Contact } from 'types/contacts'
import { formatPhone } from 'utils/phone'

const ASSOCIATION_FIELD_SET = new Set<string>([
  'schoolLocation',
  'vendorLocation',
  'organization',
] satisfies AssociationField[])

const EXTRA_DATA_FIELD_SET = new Set<string>(['extraData'] satisfies ExtraDataField[])
const EVENT_FIELD_SET = new Set<string>(['event'] satisfies EventField[])
const API_LIST_FIELD_SET = new Set<string>(['apiList'] satisfies ApiListField[])

function isAssociationField(field: string): boolean {
  return ASSOCIATION_FIELD_SET.has(field)
}

function isExtraDataField(field: string): boolean {
  return EXTRA_DATA_FIELD_SET.has(field)
}

function isEventField(field: string): boolean {
  return EVENT_FIELD_SET.has(field)
}

function isApiListField(field: string): boolean {
  return API_LIST_FIELD_SET.has(field)
}

function fieldCategory(field: string): 'assoc' | 'extra' | 'event' | 'apiList' | 'contact' {
  if (isAssociationField(field)) {
    return 'assoc'
  }
  if (isExtraDataField(field)) {
    return 'extra'
  }
  if (isEventField(field)) {
    return 'event'
  }
  if (isApiListField(field)) {
    return 'apiList'
  }
  return 'contact'
}

const FIELD_OPTIONS: { value: DynamicListRule['field']; label: string }[] = [
  { value: 'firstName', label: 'First Name' },
  { value: 'lastName', label: 'Last Name' },
  { value: 'email', label: 'Email' },
  { value: 'phoneNumber', label: 'Phone' },
  { value: 'organizationName', label: 'Organization' },
  { value: 'notes', label: 'Notes' },
  { value: 'ordoUserId', label: 'Ordo ID' },
  { value: 'schoolLocation', label: 'School Location' },
  { value: 'vendorLocation', label: 'Vendor Location' },
  { value: 'organization', label: 'Organization Assoc.' },
  { value: 'extraData', label: 'Extra Data' },
  { value: 'event', label: 'User Event' },
  { value: 'apiList', label: 'External List (API)' },
]

const EVENT_OPERATOR_OPTIONS: { value: DynamicListRule['operator']; label: string }[] = [
  { value: 'hasEvent', label: 'has event' },
  { value: 'notHasEvent', label: 'does not have event' },
]

const API_LIST_OPERATOR_OPTIONS: { value: DynamicListRule['operator']; label: string }[] = [
  { value: 'memberOf', label: 'member of' },
  { value: 'notMemberOf', label: 'not member of' },
]

const CONTACT_OPERATOR_OPTIONS: { value: DynamicListRule['operator']; label: string; needsValue: boolean }[] = [
  { value: 'equals', label: 'equals', needsValue: true },
  { value: 'notEquals', label: 'does not equal', needsValue: true },
  { value: 'contains', label: 'contains', needsValue: true },
  { value: 'notContains', label: 'does not contain', needsValue: true },
  { value: 'startsWith', label: 'starts with', needsValue: true },
  { value: 'endsWith', label: 'ends with', needsValue: true },
  { value: 'isEmpty', label: 'is empty', needsValue: false },
  { value: 'isNotEmpty', label: 'is not empty', needsValue: false },
]

const ASSOCIATION_OPERATOR_OPTIONS: { value: DynamicListRule['operator']; label: string }[] = [
  { value: 'hasAssociation', label: 'exists matching' },
  { value: 'notHasAssociation', label: 'none matching' },
]

const EXTRA_DATA_OPERATOR_OPTIONS: { value: DynamicListRule['operator']; label: string; needsValue: boolean }[] = [
  { value: 'keyExists', label: 'key exists', needsValue: false },
  { value: 'keyNotExists', label: 'key missing', needsValue: false },
  { value: 'keyEquals', label: 'value equals', needsValue: true },
  { value: 'keyContains', label: 'value contains', needsValue: true },
]

function isRuleValid(rule: DynamicListRule): boolean {
  if (isAssociationField(rule.field)) {
    return true
  }
  if (isExtraDataField(rule.field)) {
    if (!rule.key || rule.key.trim().length === 0) {
      return false
    }
    const opt = EXTRA_DATA_OPERATOR_OPTIONS.find((o) => o.value === rule.operator)
    if (!opt) {
      return false
    }
    return !opt.needsValue || (rule.value != null && rule.value.trim().length > 0)
  }
  if (isEventField(rule.field)) {
    if (!rule.eventName || rule.eventName.trim().length === 0) {
      return false
    }
    // dataMatch, if present, must have a non-empty key and at least one clause set.
    if (rule.dataMatch) {
      if (!rule.dataMatch.key || rule.dataMatch.key.trim().length === 0) {
        return false
      }
      if (rule.dataMatch.equals === undefined && (rule.dataMatch.contains ?? '').length === 0) {
        return false
      }
    }
    return true
  }
  if (isApiListField(rule.field)) {
    return Boolean(rule.apiEndpointId && rule.apiEndpointId.length > 0)
  }
  const opt = CONTACT_OPERATOR_OPTIONS.find((o) => o.value === rule.operator)
  if (!opt) {
    return false
  }
  return !opt.needsValue || (rule.value != null && rule.value.trim().length > 0)
}

function contactDisplayName(contact: Contact): string {
  return [contact.firstName, contact.lastName].filter(Boolean).join(' ') || contact.email || '(unnamed)'
}

function ContactRuleInputs({
  rule,
  onUpdate,
}: {
  rule: DynamicListRule
  onUpdate: (patch: Partial<DynamicListRule>) => void
}) {
  const needsValue = CONTACT_OPERATOR_OPTIONS.find((o) => o.value === rule.operator)?.needsValue ?? true

  return (
    <>
      <Select value={rule.operator} onValueChange={(v) => onUpdate({ operator: v as DynamicListRule['operator'] })}>
        <SelectTrigger className="w-45">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {CONTACT_OPERATOR_OPTIONS.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {needsValue && (
        <Input
          value={rule.value ?? ''}
          onChange={(e) => onUpdate({ value: e.target.value })}
          placeholder="Value..."
          className="max-w-50"
        />
      )}
    </>
  )
}

function ExtraDataRuleInputs({
  rule,
  onUpdate,
}: {
  rule: DynamicListRule
  onUpdate: (patch: Partial<DynamicListRule>) => void
}) {
  const needsValue = EXTRA_DATA_OPERATOR_OPTIONS.find((o) => o.value === rule.operator)?.needsValue ?? false

  return (
    <>
      <Input
        value={rule.key ?? ''}
        onChange={(e) => onUpdate({ key: e.target.value })}
        placeholder="Key name..."
        className="w-32"
      />
      <Select value={rule.operator} onValueChange={(v) => onUpdate({ operator: v as DynamicListRule['operator'] })}>
        <SelectTrigger className="w-36">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {EXTRA_DATA_OPERATOR_OPTIONS.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {needsValue && (
        <Input
          value={rule.value ?? ''}
          onChange={(e) => onUpdate({ value: e.target.value })}
          placeholder="Value..."
          className="max-w-50"
        />
      )}
    </>
  )
}

function AssociationMatchSelect({
  label,
  mode,
  value,
  onModeChange,
  onValueChange,
}: {
  label: string
  mode: 'any' | 'specific'
  value: string
  onModeChange: (mode: 'any' | 'specific') => void
  onValueChange: (value: string) => void
}) {
  return (
    <div className="flex items-center gap-1">
      <Select value={mode} onValueChange={(v) => onModeChange(v as 'any' | 'specific')}>
        <SelectTrigger className="w-28">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="any">Any {label}</SelectItem>
          <SelectItem value="specific">{label}:</SelectItem>
        </SelectContent>
      </Select>
      {mode === 'specific' && (
        <Input
          value={value}
          onChange={(e) => onValueChange(e.target.value)}
          placeholder={`${label}...`}
          className="w-32"
        />
      )}
    </div>
  )
}

function ApiListRuleInputs({
  rule,
  onUpdate,
}: {
  rule: DynamicListRule
  onUpdate: (patch: Partial<DynamicListRule>) => void
}) {
  const { data: endpoints } = useApiEndpoints()
  const options = endpoints ?? []

  return (
    <>
      <Select value={rule.operator} onValueChange={(v) => onUpdate({ operator: v as DynamicListRule['operator'] })}>
        <SelectTrigger className="w-40">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {API_LIST_OPERATOR_OPTIONS.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={rule.apiEndpointId ?? ''} onValueChange={(v) => onUpdate({ apiEndpointId: v || undefined })}>
        <SelectTrigger className="w-64">
          <SelectValue placeholder="Select endpoint..." />
        </SelectTrigger>
        <SelectContent>
          {options.length === 0 && (
            <SelectItem value="__none__" disabled>
              No endpoints configured
            </SelectItem>
          )}
          {options.map((ep) => (
            <SelectItem key={ep.id} value={ep.id}>
              {ep.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </>
  )
}

function EventRuleInputs({
  rule,
  onUpdate,
}: {
  rule: DynamicListRule
  onUpdate: (patch: Partial<DynamicListRule>) => void
}) {
  const window = rule.timeWindow
  const windowMode: 'any' | 'relative' = window?.kind === 'relative' ? 'relative' : 'any'
  const relativeDays = window?.kind === 'relative' ? window.days : 7

  const match = rule.dataMatch
  const dataMode: 'any' | 'equals' | 'contains' =
    match?.equals !== undefined ? 'equals' : match?.contains !== undefined ? 'contains' : 'any'

  return (
    <>
      <Select value={rule.operator} onValueChange={(v) => onUpdate({ operator: v as DynamicListRule['operator'] })}>
        <SelectTrigger className="w-45">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {EVENT_OPERATOR_OPTIONS.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        value={rule.eventName ?? ''}
        onChange={(e) => onUpdate({ eventName: e.target.value })}
        placeholder="event_name"
        className="w-44"
        list="contact-event-names"
      />
      <Select
        value={windowMode}
        onValueChange={(v) => {
          if (v === 'any') {
            onUpdate({ timeWindow: undefined })
          } else {
            onUpdate({ timeWindow: { kind: 'relative', days: relativeDays } })
          }
        }}>
        <SelectTrigger className="w-32">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="any">any time</SelectItem>
          <SelectItem value="relative">in last…</SelectItem>
        </SelectContent>
      </Select>
      {windowMode === 'relative' && (
        <>
          <Input
            type="number"
            min={1}
            value={relativeDays}
            onChange={(e) => {
              const days = Math.max(1, Number(e.target.value) || 1)
              onUpdate({ timeWindow: { kind: 'relative', days } })
            }}
            className="w-16"
          />
          <span className="text-sm text-muted-foreground">days</span>
        </>
      )}
      <Select
        value={dataMode}
        onValueChange={(v) => {
          if (v === 'any') {
            onUpdate({ dataMatch: undefined })
          } else if (v === 'equals') {
            onUpdate({ dataMatch: { key: match?.key ?? '', equals: '' } })
          } else {
            onUpdate({ dataMatch: { key: match?.key ?? '', contains: '' } })
          }
        }}>
        <SelectTrigger className="w-32">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="any">any data</SelectItem>
          <SelectItem value="equals">data equals</SelectItem>
          <SelectItem value="contains">data contains</SelectItem>
        </SelectContent>
      </Select>
      {dataMode !== 'any' && (
        <>
          <Input
            value={match?.key ?? ''}
            onChange={(e) =>
              onUpdate({
                dataMatch:
                  dataMode === 'equals'
                    ? { key: e.target.value, equals: match?.equals ?? '' }
                    : { key: e.target.value, contains: match?.contains ?? '' },
              })
            }
            placeholder="key"
            className="w-28"
          />
          <Input
            value={dataMode === 'equals' ? String(match?.equals ?? '') : (match?.contains ?? '')}
            onChange={(e) =>
              onUpdate({
                dataMatch:
                  dataMode === 'equals'
                    ? { key: match?.key ?? '', equals: e.target.value }
                    : { key: match?.key ?? '', contains: e.target.value },
              })
            }
            placeholder="value"
            className="w-28"
          />
        </>
      )}
    </>
  )
}

function AssociationRuleInputs({
  rule,
  onUpdate,
}: {
  rule: DynamicListRule
  onUpdate: (patch: Partial<DynamicListRule>) => void
}) {
  return (
    <>
      <Select value={rule.operator} onValueChange={(v) => onUpdate({ operator: v as DynamicListRule['operator'] })}>
        <SelectTrigger className="w-45">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {ASSOCIATION_OPERATOR_OPTIONS.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <AssociationMatchSelect
        label="Role"
        mode={rule.role != null ? 'specific' : 'any'}
        value={rule.role ?? ''}
        onModeChange={(m) => onUpdate({ role: m === 'any' ? undefined : '' })}
        onValueChange={(v) => onUpdate({ role: v || undefined })}
      />
      <AssociationMatchSelect
        label="ID"
        mode={rule.externalId != null ? 'specific' : 'any'}
        value={rule.externalId ?? ''}
        onModeChange={(m) => onUpdate({ externalId: m === 'any' ? undefined : '' })}
        onValueChange={(v) => onUpdate({ externalId: v || undefined })}
      />
    </>
  )
}

export function RuleBuilder({ ruleset, onChange }: { ruleset: Ruleset; onChange: (ruleset: Ruleset) => void }) {
  const { perform: preview, loading: previewing } = usePreviewDynamicList()
  const [previewData, setPreviewData] = useState<{ contacts: Contact[]; total: number } | null>(null)
  const previewVersion = useRef(0)

  const { match, rules } = ruleset

  // Debounced preview
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
    }
    const validRules = rules.filter(isRuleValid)
    if (validRules.length === 0) {
      setPreviewData(null)
      return
    }
    const version = ++previewVersion.current
    timerRef.current = setTimeout(() => {
      preview({ match, rules: validRules }).then((result) => {
        if (version === previewVersion.current) {
          setPreviewData(result)
        }
      })
    }, 400)

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(ruleset)])

  const setMatch = useCallback(
    (m: RuleMatch) => {
      onChange({ ...ruleset, match: m })
    },
    [ruleset, onChange],
  )

  const addRule = useCallback(() => {
    onChange({ ...ruleset, rules: [...rules, { field: 'firstName', operator: 'contains', value: '' }] })
  }, [ruleset, rules, onChange])

  const removeRule = useCallback(
    (index: number) => {
      onChange({ ...ruleset, rules: rules.filter((_, i) => i !== index) })
    },
    [ruleset, rules, onChange],
  )

  const updateRule = useCallback(
    (index: number, patch: Partial<DynamicListRule>) => {
      onChange({
        ...ruleset,
        rules: rules.map((r, i) => {
          if (i !== index) {
            return r
          }
          const updated = { ...r, ...patch }
          // Reset operator and value fields when switching between field categories
          if (patch.field) {
            const oldCategory = fieldCategory(r.field)
            const newCategory = fieldCategory(patch.field)
            if (oldCategory !== newCategory) {
              if (newCategory === 'assoc') {
                return { field: patch.field, operator: 'hasAssociation' as const }
              }
              if (newCategory === 'extra') {
                return { field: patch.field, operator: 'keyExists' as const, key: '' }
              }
              if (newCategory === 'event') {
                return { field: patch.field, operator: 'hasEvent' as const, eventName: '' }
              }
              if (newCategory === 'apiList') {
                return { field: patch.field, operator: 'memberOf' as const }
              }
              return { field: patch.field, operator: 'contains' as const, value: '' }
            }
          }
          return updated
        }),
      })
    },
    [ruleset, rules, onChange],
  )

  const data = previewData

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-muted-foreground">Match</label>
            <Select value={match} onValueChange={(v) => setMatch(v as RuleMatch)}>
              <SelectTrigger className="w-28">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All rules</SelectItem>
                <SelectItem value="any">Any rule</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={addRule}>
            <Plus className="mr-1 h-3 w-3" />
            Add Rule
          </Button>
        </div>
        {rules.length === 0 && (
          <p className="text-sm text-muted-foreground">No rules defined. Add a rule to match contacts.</p>
        )}
        {rules.map((rule, index) => (
          <div key={index} className="flex flex-wrap items-center gap-2">
            <Select
              value={rule.field}
              onValueChange={(v) => updateRule(index, { field: v as DynamicListRule['field'] })}>
              <SelectTrigger className="w-44">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FIELD_OPTIONS.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {fieldCategory(rule.field) === 'assoc' && (
              <AssociationRuleInputs rule={rule} onUpdate={(patch) => updateRule(index, patch)} />
            )}
            {fieldCategory(rule.field) === 'extra' && (
              <ExtraDataRuleInputs rule={rule} onUpdate={(patch) => updateRule(index, patch)} />
            )}
            {fieldCategory(rule.field) === 'event' && (
              <EventRuleInputs rule={rule} onUpdate={(patch) => updateRule(index, patch)} />
            )}
            {fieldCategory(rule.field) === 'apiList' && (
              <ApiListRuleInputs rule={rule} onUpdate={(patch) => updateRule(index, patch)} />
            )}
            {fieldCategory(rule.field) === 'contact' && (
              <ContactRuleInputs rule={rule} onUpdate={(patch) => updateRule(index, patch)} />
            )}
            <Button type="button" variant="ghost" size="sm" onClick={() => removeRule(index)}>
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        ))}
      </div>

      {rules.length > 0 && (
        <>
          <Separator />
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-muted-foreground">Preview</label>
              {previewing && <span className="text-xs text-muted-foreground">Loading...</span>}
              {data && (
                <Badge variant="secondary">
                  {data.total} contact{data.total !== 1 ? 's' : ''} matching
                </Badge>
              )}
            </div>
            {data && data.contacts.length > 0 ? (
              <div className="overflow-x-auto rounded-md border border-border">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/50">
                      <th className="px-3 py-2 text-left font-medium text-muted-foreground">Name</th>
                      <th className="px-3 py-2 text-left font-medium text-muted-foreground">Email</th>
                      <th className="px-3 py-2 text-left font-medium text-muted-foreground">Phone</th>
                      <th className="px-3 py-2 text-left font-medium text-muted-foreground">Organization</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.contacts.map((contact) => (
                      <tr key={contact.id} className="border-b border-border last:border-0">
                        <td className="px-3 py-2">{contactDisplayName(contact)}</td>
                        <td className="px-3 py-2 text-muted-foreground">{contact.email || '-'}</td>
                        <td className="px-3 py-2 text-muted-foreground">{formatPhone(contact.phoneNumber) || '-'}</td>
                        <td className="px-3 py-2 text-muted-foreground">{contact.organizationName || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {data.total > data.contacts.length && (
                  <div className="border-t border-border px-3 py-2 text-center text-xs text-muted-foreground">
                    Showing {data.contacts.length} of {data.total}
                  </div>
                )}
              </div>
            ) : (
              data && <p className="text-sm text-muted-foreground">No contacts match these rules.</p>
            )}
          </div>
        </>
      )}
    </div>
  )
}
